import { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  Tooltip, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import {
  BarChart3, TrendingUp, TrendingDown, FileSpreadsheet, FileText,
  FileType, Presentation, Users, Building2, Loader2, Star, CalendarCheck,
} from "lucide-react";

const COLORS = ["#1F4E79", "#4A7C59", "#F4A261", "#E63946", "#8d6e9e", "#57534E"];
const AXIS_TICK = { fontSize: 11 };
const RSVP_DOT = { r: 3 };
const RSVP_MARGIN = { left: 20 };
const EXPORTS = [
  { fmt: "excel", ext: "xlsx", label: "Excel", icon: FileSpreadsheet },
  { fmt: "word", ext: "docx", label: "Word", icon: FileText },
  { fmt: "pdf", ext: "pdf", label: "PDF", icon: FileType },
  { fmt: "powerpoint", ext: "pptx", label: "PowerPoint", icon: Presentation },
];

function Kpi({ label, current, previous }) {
  const diff = current - previous;
  const up = diff >= 0;
  return (
    <div className="bg-white border border-line rounded-md p-4" data-testid="kpi-card">
      <p className="text-xs uppercase tracking-wider font-bold text-muted2">{label} (30d)</p>
      <p className="font-heading font-black text-3xl text-ink mt-1">{current}</p>
      <p className={`text-xs font-semibold mt-1 inline-flex items-center gap-1 ${up ? "text-calm" : "text-critical"}`}>
        {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
        {up ? "+" : ""}{diff} vs prior 30d
      </p>
    </div>
  );
}

export function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [grain, setGrain] = useState("daily");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  const download = async (fmt, ext) => {
    setBusy(fmt);
    try {
      const res = await api.get(`/admin/reports/export?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `hutanohwevanhu-report.${ext}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt} report downloaded`);
    } catch (e) { toast.error("Export failed"); }
    finally { setBusy(""); }
  };

  if (!data) return <p className="text-muted2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…</p>;

  const roleData = Object.entries(data.users_by_role).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));
  const clinicData = data.by_clinic.filter((c) => c.total > 0).map((c) => ({ name: c.clinic.replace(" (broadcast)", ""), Announcements: c.announcements, Events: c.events, Inventory: c.inventory }));
  const ts = (data.timeseries.announcements[grain] || []).map((d) => ({
    period: d.period,
    Announcements: d.count,
    Events: (data.timeseries.events[grain].find((x) => x.period === d.period) || {}).count || 0,
    Inventory: (data.timeseries.inventory[grain].find((x) => x.period === d.period) || {}).count || 0,
  }));

  return (
    <div data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-2"><BarChart3 className="w-7 h-7 text-clinical" /> Reporting & Analytics</h1>
        <div className="flex flex-wrap gap-2">
          {EXPORTS.map((e) => (
            <button key={e.fmt} onClick={() => download(e.fmt, e.ext)} disabled={busy === e.fmt} data-testid={`export-${e.fmt}-btn`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 hover:bg-clinical/5 disabled:opacity-50">
              {busy === e.fmt ? <Loader2 className="w-4 h-4 animate-spin" /> : <e.icon className="w-4 h-4" />} {e.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi label="Announcements" {...data.kpi.announcements} />
        <Kpi label="Events" {...data.kpi.events} />
        <Kpi label="Inventory" {...data.kpi.inventory} />
        <Kpi label="Appointments" {...data.kpi.appointments} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-line rounded-md p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg text-ink">Content posted over time</h2>
            <div className="flex gap-1">
              {["daily", "monthly", "yearly"].map((g) => (
                <button key={g} onClick={() => setGrain(g)} data-testid={`grain-${g}`}
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${grain === g ? "bg-clinical text-white" : "text-muted2 border border-line"}`}>{g}</button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={ts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="period" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="Announcements" stroke="#1F4E79" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Events" stroke="#4A7C59" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Inventory" stroke="#F4A261" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-line rounded-md p-5">
          <h2 className="font-heading font-bold text-lg text-ink mb-2 flex items-center gap-2"><Users className="w-5 h-5 text-clinical" /> Users by role</h2>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={roleData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {roleData.map((r, i) => <Cell key={r.name} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 justify-center text-xs">
            {roleData.map((r, i) => (
              <span key={r.name} className="inline-flex items-center gap-1 capitalize"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /> {r.name}: {r.value}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-line rounded-md p-5 mb-8">
        <h2 className="font-heading font-bold text-lg text-ink mb-4 flex items-center gap-2"><Building2 className="w-5 h-5 text-clinical" /> Posts by clinic</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={clinicData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
            <XAxis dataKey="name" tick={AXIS_TICK} />
            <YAxis tick={AXIS_TICK} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="Announcements" stackId="a" fill="#1F4E79" />
            <Bar dataKey="Events" stackId="a" fill="#4A7C59" />
            <Bar dataKey="Inventory" stackId="a" fill="#F4A261" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white border border-line rounded-md p-4">
          <p className="text-xs uppercase tracking-wider font-bold text-muted2">Public questions asked</p>
          <p className="font-heading font-black text-3xl text-ink mt-1">{data.public_engagement.questions_asked}</p>
        </div>
        <div className="bg-white border border-line rounded-md p-4">
          <p className="text-xs uppercase tracking-wider font-bold text-muted2">Alert subscribers</p>
          <p className="font-heading font-black text-3xl text-ink mt-1">{data.public_engagement.push_subscribers}</p>
        </div>
        <div className="bg-white border border-line rounded-md p-4">
          <p className="text-xs uppercase tracking-wider font-bold text-muted2">Clinics</p>
          <p className="font-heading font-black text-3xl text-ink mt-1">{data.totals.clinics}</p>
        </div>
        <div className="bg-white border border-line rounded-md p-4" data-testid="stat-appointment-requests">
          <p className="text-xs uppercase tracking-wider font-bold text-muted2">Appointment requests made</p>
          <p className="font-heading font-black text-3xl text-ink mt-1">{data.totals.appointment_requests ?? 0}</p>
          <p className="text-xs text-muted2 mt-1">{data.totals.appointment_requests_open ?? 0} currently open</p>
        </div>
      </div>

      {data.feedback && (
        <div className="bg-white border border-line rounded-md p-5 mt-8" data-testid="satisfaction-section">
          <h2 className="font-heading font-bold text-lg text-ink mb-4 flex items-center gap-2"><Star className="w-5 h-5 text-alert fill-alert" /> Patient satisfaction</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="flex flex-col items-center justify-center border border-line rounded-md p-4">
              <p className="font-heading font-black text-5xl text-ink" data-testid="satisfaction-average">{data.feedback.average || "—"}</p>
              <div className="flex items-center gap-0.5 mt-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-4 h-4 ${Math.round(data.feedback.average) >= n ? "fill-alert text-alert" : "text-line"}`} />
                ))}
              </div>
              <p className="text-xs text-muted2 mt-2">{data.feedback.count} rating{data.feedback.count === 1 ? "" : "s"} · {data.feedback.response_rate}% response</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs uppercase tracking-wider font-bold text-muted2 mb-2">Rating distribution</p>
              {[5, 4, 3, 2, 1].map((star) => {
                const c = data.feedback.distribution[String(star)] || 0;
                const pct = data.feedback.count ? Math.round((c / data.feedback.count) * 100) : 0;
                return (
                  <div key={star} className="flex items-center gap-2 mb-1.5 text-sm">
                    <span className="w-10 inline-flex items-center gap-0.5 text-muted2">{star} <Star className="w-3 h-3 fill-alert text-alert" /></span>
                    <div className="flex-1 bg-line/50 rounded-full h-2.5 overflow-hidden"><div className="h-full bg-alert" style={{ width: `${pct}%` }} /></div>
                    <span className="w-10 text-right text-muted2">{c}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {data.feedback.by_specialist.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider font-bold text-muted2 mb-2">By specialist</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.feedback.by_specialist.map((s) => (
                  <div key={s.specialist} className="flex items-center justify-between border border-line rounded px-3 py-2 text-sm" data-testid="satisfaction-specialist">
                    <span className="font-semibold text-ink truncate">{s.specialist}</span>
                    <span className="inline-flex items-center gap-1 text-muted2 shrink-0"><Star className="w-3.5 h-3.5 fill-alert text-alert" /> {s.avg} ({s.count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {data.feedback.recent_notes.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider font-bold text-muted2 mb-2">Recent comments</p>
              <div className="space-y-2">
                {data.feedback.recent_notes.map((n, i) => (
                  <div key={`${n.specialist || "note"}-${n.at || i}`} className="border-l-2 border-alert/50 pl-3 py-1 text-sm">
                    <span className="inline-flex items-center gap-1 text-alert font-semibold">{n.rating}<Star className="w-3 h-3 fill-alert text-alert" /></span>
                    <span className="text-muted2"> · {n.specialist} — “{n.note}”</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {data.rsvp && (
        <div className="bg-white border border-line rounded-md p-5 mt-8" data-testid="rsvp-section">
          <h2 className="font-heading font-bold text-lg text-ink mb-4 flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-clinical" /> Attendance &amp; RSVPs</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {[
              { label: "Total RSVPs", value: data.rsvp.total, testid: "rsvp-total" },
              { label: "Attending", value: data.rsvp.by_status?.attending || 0, testid: "rsvp-attending" },
              { label: "Interested", value: data.rsvp.by_status?.interested || 0, testid: "rsvp-interested" },
              { label: "Tentative", value: data.rsvp.by_status?.tentative || 0, testid: "rsvp-tentative" },
              { label: "Registered patients", value: data.rsvp.registered_patients, testid: "rsvp-registered" },
              { label: "Anonymous", value: data.rsvp.anonymous, testid: "rsvp-anonymous" },
            ].map((c) => (
              <div key={c.label} data-testid={c.testid} className="border border-line rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-muted2 leading-tight">{c.label}</p>
                <p className="font-heading font-black text-2xl text-ink mt-1">{c.value}</p>
              </div>
            ))}
          </div>
          {data.rsvp.monthly_trend?.length > 0 && (
            <div className="mb-6">
              <p className="text-xs uppercase tracking-wider font-bold text-muted2 mb-2">Attendance trend (month over month)</p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.rsvp.monthly_trend.map((d) => ({ period: d.period, RSVPs: d.count }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="period" tick={AXIS_TICK} />
                  <YAxis tick={AXIS_TICK} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="RSVPs" stroke="#0d6e6e" strokeWidth={2} dot={RSVP_DOT} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {data.rsvp.top_events?.length > 0 ? (
            <div>
              <p className="text-xs uppercase tracking-wider font-bold text-muted2 mb-2">Top events by RSVPs</p>
              <ResponsiveContainer width="100%" height={Math.max(160, data.rsvp.top_events.length * 42)}>
                <BarChart layout="vertical" data={data.rsvp.top_events.map((e) => ({ name: e.title, Attending: e.attending, Interested: e.interested, Tentative: e.tentative }))} margin={RSVP_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis type="number" tick={AXIS_TICK} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={AXIS_TICK} width={140} />
                  <Tooltip />
                  <Bar dataKey="Attending" stackId="r" fill="#4A7C59" />
                  <Bar dataKey="Interested" stackId="r" fill="#1F4E79" />
                  <Bar dataKey="Tentative" stackId="r" fill="#F4A261" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-muted2">No RSVPs recorded yet.</p>
          )}
        </div>
      )}
    </div>
  );
}